"use client";

import Link from "next/link";
import { CalendarClock, CheckCircle2, ChevronRight, Clock3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CALENDAR_LANE_META, laneOf } from "@/lib/types/calendar";
import { daysUntil, formatDisplayDate } from "@/lib/utils/dateHelpers";
import type { AttentionItem, HomeAttention } from "@/lib/hooks/use-home-attention";

/** Most rows we show before deferring to the full calendar. */
const MAX_ROWS = 6;

/** "5 days late" / "Today" / "Tomorrow" / "Thu" / "Sep 12" */
function whenLabel(item: AttentionItem): string {
  const days = daysUntil(item.date);
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? "" : "s"} late`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days <= 6) return formatDisplayDate(item.date, { weekday: "long" });
  return formatDisplayDate(item.date, { month: "short", day: "numeric" });
}

interface AttentionPanelProps extends HomeAttention {
  /** Whether the unified Calendar page is worth linking to (2+ calendar hubs). */
  showCalendarLink: boolean;
}

/**
 * The home page's "what needs me" list, below the app grid. Every row comes from
 * `/api/calendar/unified`, which only returns events from hubs the caller already
 * has access to — so a teacher never sees a compliance deadline here, and an
 * owner without the Compliance Hub never sees one either.
 */
export function AttentionPanel({
  items,
  counts,
  ptoPending,
  isLoading,
  enabled,
  showCalendarLink,
}: AttentionPanelProps) {
  // Nothing to say for a user with no calendar-bearing hub — don't take up the space.
  if (!enabled || isLoading) return null;

  const rows = items.slice(0, MAX_ROWS);
  const overflow = items.length - rows.length;
  const isClear = items.length === 0 && ptoPending === 0;

  return (
    <div className="mt-10 md:mt-14 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-sm font-semibold text-gray-700 tracking-wide uppercase">
          Needs your attention
        </h2>
        {showCalendarLink && (
          <Link
            href="/calendar"
            className="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-0.5"
          >
            View calendar
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      <div className="rounded-2xl bg-white/80 backdrop-blur-sm ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_24px_-10px_rgba(0,0,0,0.18)] overflow-hidden">
        {isClear ? (
          <div className="flex items-center gap-3 px-4 py-5 text-sm text-gray-500">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
            You’re all caught up — nothing due in the next week.
          </div>
        ) : (
          <>
            {ptoPending > 0 && (
              <Link
                href="/documents/pto"
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/80 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <Clock3 className="h-4 w-4 text-rose-500 shrink-0" />
                <span className="text-sm text-gray-800 flex-1 min-w-0 truncate">
                  {ptoPending} time-off request{ptoPending === 1 ? "" : "s"} waiting on you
                </span>
                <span className="text-xs font-medium text-rose-600 shrink-0">Review</span>
              </Link>
            )}

            {rows.map((item) => {
              const lane = CALENDAR_LANE_META[laneOf(item)];
              return (
                <Link
                  key={item.id}
                  href={item.link}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/80 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <span className={cn("h-2 w-2 rounded-full shrink-0", lane.dotClass)} />
                  <span className="min-w-0 flex-1">
                    <span className="text-sm text-gray-800 block truncate">{item.title}</span>
                    {item.subtitle && (
                      <span className="text-xs text-gray-500 block truncate">{item.subtitle}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium shrink-0",
                      item.bucket === "overdue"
                        ? "text-red-600"
                        : item.bucket === "today"
                          ? "text-amber-600"
                          : "text-gray-400",
                    )}
                  >
                    {whenLabel(item)}
                  </span>
                </Link>
              );
            })}

            {overflow > 0 && (
              <Link
                href="/calendar"
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/80 transition-colors text-xs font-medium text-gray-500"
              >
                <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                {overflow} more in the next week
                {counts.overdue > 0 && ` · ${counts.overdue} overdue`}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
